import React from "react";
import { Modal, Fade, Box, Backdrop } from "@mui/material";
import { Theme } from "@mui/material/styles";

// width / height props arrive as a number (px), an "NNNpx" string, "auto"
// or "NNvh".
const cssLength = (value: number | string) =>
  typeof value === "number" ? `${value}px` : value;

// Caps a length at `limit`. Keywords such as "auto" are not lengths and are
// invalid inside min(), so they pass through unchanged.
const capAt = (value: string, limit: string) =>
  /^[a-z-]+$/i.test(value) ? value : `min(${value}, ${limit})`;

// Keep the box absolutely positioned and centred with a transform: sticky
// Save/Send bars inside some dialogs are position:fixed and stay inside the
// box only because of that transform. Keep a definite height too: several
// dialog layouts use height:100% inside it.
const ThriveModal = (props: any) => {
  const width = cssLength(props.width ? props.width : 960);
  const height = cssLength(props.height ? props.height : 650);
  // Never taller than the screen. dvh follows the visible area on mobile
  // browsers; vh is the fallback.
  const fitHeight = (unit: "vh" | "dvh") =>
    capAt(height, `calc(100${unit} - 24px)`);

  const style = (theme: Theme) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: "calc(100% - 16px)",
      md: capAt(width, "calc(100% - 48px)"),
    },
    height: fitHeight("vh"),
    "@supports (height: 100dvh)": { height: fitHeight("dvh") },
    maxHeight: "95vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    borderRadius: "10px",
    ...(props.fullScreenOnPhone && {
      [theme.breakpoints.down("sm")]: {
        width: "100vw",
        height: "100vh",
        "@supports (height: 100dvh)": { height: "100dvh" },
        maxHeight: "none",
        border: "none",
        borderRadius: 0,
      },
    }),
  });

  const handleClose = () => {
    if (props.closeByClick) {
      props.setOpen(false);
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={props.open}>
        <Box sx={style} style={{ padding: 0 }}>
          {props.contentComponent}
        </Box>
      </Fade>
    </Modal>
  );
};

export default ThriveModal;
